// Industry-specific content, driving both the /industries hub and each
// /industries/:slug detail page. Cross-links point at real service slugs
// from servicesMenuConfig.js or the Managed IT / Development pillar pages —
// never a route that doesn't exist.
const risk = (title, desc) => ({ title, desc });
const svc = (label, href) => ({ label, href });
const framework = (name, region) => ({ name, region });

export const industries = [
  {
    slug: 'financial-services',
    icon: 'landmark',
    label: 'Financial Services',
    eyebrow: 'Industries',
    heroTitle: 'Security Built for Financial Services',
    heroDesc: 'Lending platforms, payment flows, and core banking systems are high-value targets and heavily regulated. We help financial services firms pass the audit and stay off the incident report.',
    stats: [
      { value: '24/7', label: 'SOC Coverage' },
      { value: '<30 min', label: 'Mean Time to Triage' },
      { value: '100%', label: 'Critical Findings Remediated' },
    ],
    risks: [
      risk('API-first architecture, wider attack surface', 'Open banking and partner integrations mean more APIs handling money movement and account data — each one is a potential authorization bypass waiting to be found.'),
      risk('Third-party and vendor risk', 'Most fintechs and NBFCs run on a stack of external processors, KYC vendors, and core banking partners. A weakness in any one of them becomes your incident.'),
      risk('Insider and privileged access risk', 'Financial systems concentrate value in a small number of privileged accounts — the ones most worth monitoring and least often reviewed.'),
      risk('Regulatory audit cycles', 'RBI, SEBI, and FCA expectations move faster than most internal security roadmaps, and falling behind shows up in the next audit, not gradually.'),
    ],
    services: [
      svc('Penetration Testing (VAPT)', '/services/vapt'),
      svc('Web Application Security Testing', '/services/web-application-penetration-testing'),
      svc('vCISO Services', '/services/vciso-services'),
      svc('Security Operations Centre (24/7 SOC)', '/services/security-operations-centre'),
      svc('Security Consulting', '/services/cyber-security-strategic-consulting'),
      svc('ISO 27001 Consulting', '/services/iso-27001-consulting'),
    ],
    frameworks: [
      framework('RBI Cybersecurity Framework', 'in'),
      framework('SEBI CSCRF 2024', 'in'),
      framework('NIST CSF 2.0', 'both'),
      framework('FCA Operational Resilience', 'uk'),
      framework('ISO/IEC 27001:2022', 'both'),
    ],
    caseStudyRef: 'Mid-Market NBFC & Lending Platform',
  },
  {
    slug: 'healthcare',
    icon: 'hospital',
    label: 'Healthcare',
    eyebrow: 'Industries',
    heroTitle: 'Security Built for Healthcare',
    heroDesc: 'Patient records, diagnostic systems, and connected medical devices cannot go down and cannot leak. We help hospital groups and clinics close the gaps ransomware actors look for first.',
    stats: [
      { value: '24/7', label: 'SOC Coverage' },
      { value: '100%', label: 'Audit Success Rate' },
      { value: '4 wk', label: 'To Compliance Kickoff' },
    ],
    risks: [
      risk('Ransomware targets hospitals specifically', 'Healthcare cannot tolerate downtime the way other sectors can, which is exactly why ransomware operators prioritize it — the pressure to pay is built into the business.'),
      risk('Legacy clinical systems that cannot be patched on a normal cycle', 'Diagnostic and imaging equipment often runs on operating systems the vendor no longer updates, which means the fix has to be network segmentation, not a patch.'),
      risk('Multi-site network sprawl', 'Hospital groups spanning several facilities accumulate inconsistent segmentation between clinical and administrative networks over time, usually without anyone deciding that on purpose.'),
      risk('Connected medical devices and IoT', 'Infusion pumps, monitors, and imaging systems are now network-attached endpoints, and most were never designed with an adversarial network in mind.'),
    ],
    services: [
      svc('Network Penetration Testing', '/services/network-penetration-testing'),
      svc('ISO 27001 Consulting', '/services/iso-27001-consulting'),
      svc('VAPT (Combined Vulnerability & Penetration Testing)', '/services/vapt'),
      svc('Managed Security Services', '/services/managed-security-services'),
      svc('Security Architecture Review', '/services/security-architecture-review'),
      svc('Backup & Disaster Recovery (Managed IT)', '/managed-it'),
    ],
    frameworks: [
      framework('ISO/IEC 27001:2022', 'both'),
      framework('DPDP Act 2023', 'in'),
      framework('UK GDPR & DPA 2018', 'uk'),
      framework('Cyber Essentials & CE+', 'uk'),
    ],
    caseStudyRef: 'Regional Multi-Specialty Hospital Group',
  },
  {
    slug: 'saas-technology',
    icon: 'cloud',
    label: 'SaaS & Technology',
    eyebrow: 'Industries',
    heroTitle: 'Security Built for SaaS & Technology',
    heroDesc: 'Enterprise buyers ask for ISO 27001 before they will sign, and your engineering team ships weekly. We build security into the pipeline instead of bolting it onto the release.',
    stats: [
      { value: 'ISO 27001', label: 'Certification Ready' },
      { value: '24h', label: 'Pentest Response Time' },
      { value: '500+', label: 'Applications Hardened' },
    ],
    risks: [
      risk('Multi-tenancy isolation failures', 'A single broken authorization check in a multi-tenant application does not expose one customer\'s data — it can expose all of them, which is why this is the first thing we test.'),
      risk('Compliance as a sales blocker, not a roadmap item', 'When enterprise procurement asks for ISO 27001 and you do not have it, the deal stalls — often at the exact moment you can least afford the delay.'),
      risk('API sprawl from rapid feature shipping', 'Every new integration and internal microservice is a new authentication boundary, and boundaries added under deadline pressure are where authorization bugs live.'),
      risk('CI/CD pipelines shipping faster than security review', 'Weekly or daily deploys leave little room for manual security review unless checks are built into the pipeline itself, not bolted on before release.'),
    ],
    services: [
      svc('Web Application Security Testing', '/services/web-application-penetration-testing'),
      svc('Cloud Security', '/services/cloud-security-audit'),
      svc('NIST CSF 2.0 Implementation', '/services/nist-csf-implementation'),
      svc('ISO 27001 Consulting', '/services/iso-27001-consulting'),
      svc('Managed Security Monitoring', '/services/managed-security-services'),
      svc('Security Architecture Review', '/services/security-architecture-review'),
    ],
    frameworks: [
      framework('NIST CSF 2.0', 'both'),
      framework('ISO/IEC 27001:2022', 'both'),
      framework('GDPR / UK GDPR', 'both'),
      framework('DPDP Act 2023', 'in'),
    ],
    caseStudyRef: null,
  },
  {
    slug: 'ecommerce-retail',
    icon: 'store',
    label: 'E-commerce & Retail',
    eyebrow: 'Industries',
    heroTitle: 'Security Built for E-commerce & Retail',
    heroDesc: 'Your busiest security day is also your busiest sales day. We test and monitor storefronts, checkout flows, and payment integrations so a traffic spike does not turn into an incident.',
    stats: [
      { value: 'CIS', label: 'Benchmark Hardening' },
      { value: '24/7', label: 'Peak-Season Monitoring' },
      { value: '500+', label: 'Applications Hardened' },
    ],
    risks: [
      risk('Card data scope creep', 'Every integration that touches payment data — even briefly — pulls more of your environment into compliance scope, and most retailers do not realize how far that scope has spread until it becomes a costly problem.'),
      risk('Bot-driven credential stuffing on login and checkout', 'Attackers test stolen credential lists against login forms and gift-card balance checks at scale, and a checkout flow with no rate limiting is an open invitation.'),
      risk('Third-party plugin and theme supply chain risk', 'Most storefronts run on a stack of third-party plugins and themes that are rarely audited individually, and a single vulnerable one can expose the entire storefront.'),
      risk('Availability and security in tension during sale events', 'The traffic surge during a major sale is exactly when defenses get relaxed to avoid false positives blocking real customers — which is exactly when attackers expect that to happen.'),
    ],
    services: [
      svc('Web Application Security Testing', '/services/web-application-penetration-testing'),
      svc('CIS Controls Implementation', '/services/cis-controls-implementation'),
      svc('Penetration Testing (VAPT)', '/services/vapt'),
      svc('Cloud Security', '/services/cloud-security-audit'),
      svc('Managed Security Monitoring', '/services/managed-security-services'),
      svc('Incident Response', '/services/incident-response'),
    ],
    frameworks: [
      framework('CIS Controls v8.1', 'both'),
      framework('ISO/IEC 27001:2022', 'both'),
      framework('DPDP Act 2023', 'in'),
      framework('UK GDPR & DPA 2018', 'uk'),
    ],
    caseStudyRef: 'Retail Chain with E-commerce Platform',
  },
  {
    slug: 'government-public-sector',
    icon: 'university',
    label: 'Government & Public Sector',
    eyebrow: 'Industries',
    heroTitle: 'Security Built for Government & Public Sector',
    heroDesc: 'Citizen-facing portals and critical infrastructure carry a different threat model than commercial systems, with budgets that rarely match the stakes. We help public sector teams get real coverage without a private-sector headcount.',
    stats: [
      { value: '24/7', label: 'SOC Coverage' },
      { value: 'ISO', label: 'Certified Advisors' },
      { value: '4 wk', label: 'To Compliance Kickoff' },
    ],
    risks: [
      risk('Critical infrastructure as a stated target', 'Public sector and critical infrastructure systems are named targets in national threat assessments, not incidental victims of opportunistic attacks.'),
      risk('Legacy systems and modernization gaps', 'Public-facing citizen service portals often run on infrastructure that predates modern security practices, patched around rather than rebuilt, because full modernization is a multi-year budget line.'),
      risk('Strict audit trail and compliance obligations', 'NCIIPC guidelines and NCSC CAF assessments expect evidence, not intent — and evidence has to be built into operations from day one, not assembled before an audit.'),
      risk('Security teams stretched thin', 'Budget-constrained IT teams are usually running security as one responsibility among many, without the headcount for dedicated 24/7 coverage.'),
    ],
    services: [
      svc('Security Architecture Review', '/services/security-architecture-review'),
      svc('Security Operations Centre (24/7 SOC)', '/services/security-operations-centre'),
      svc('Penetration Testing (VAPT)', '/services/vapt'),
      svc('Security Consulting', '/services/cyber-security-strategic-consulting'),
      svc('Zero Trust Architecture', '/services/zero-trust-architecture'),
      svc('NCSC CAF Assessment', '/services/ncsc-caf-assessment'),
    ],
    frameworks: [
      framework('NCIIPC Guidelines', 'in'),
      framework('NCSC CAF', 'uk'),
      framework('ISO/IEC 27001:2022', 'both'),
      framework('IT Act 2000 (+2008)', 'in'),
    ],
    caseStudyRef: null,
  },
];

export const getIndustryBySlug = (slug) => industries.find((i) => i.slug === slug);
